import { Document } from '../types/Document';

// Base paths for assets - using R2 custom domain
export const ASSETS_BASE = 'https://assets.democracyrenaissance.org';
export const TEXT_BASE = `${ASSETS_BASE}/extracted-text`;
export const IMAGE_BASE = `${ASSETS_BASE}/web-images`;

// Available folders (001-012)
export const AVAILABLE_FOLDERS = ['001', '002', '003', '004', '005', '006', '007', '008', '009', '010', '011', '012'];

// Extract document ID from filename
export const extractDocumentId = (filename: string): string => {
  const match = filename.match(/HOUSE_OVERSIGHT_(\d+)/);
  return match ? match[1] : '';
};

// Generate document object from filename and folder (both text and image use same folder number)
export const createDocument = (filename: string, folder: string): Document => {
  const id = extractDocumentId(filename);
  const baseName = filename.replace(/\.(txt|jpg)$/, '');
  
  return {
    id,
    filename: baseName,
    folder,
    imagePath: `${IMAGE_BASE}/${folder}/${baseName}.jpg`,
    thumbnailPath: `${IMAGE_BASE}/${folder}/${baseName}-thumbnail.jpg`,
    textPath: `${TEXT_BASE}/${folder}/${baseName}.txt`,
  };
};

// Load text content for a document
export const loadDocumentText = async (textPath: string): Promise<string> => {
  try {
    const response = await fetch(textPath);
    if (!response.ok) {
      throw new Error(`Failed to load text: ${response.statusText}`);
    }
    return await response.text();
  } catch (error) {
    console.error('Error loading document text:', error);
    return 'Text content could not be loaded.';
  }
};

// Check if image exists
export const checkImageExists = async (imagePath: string): Promise<boolean> => {
  try {
    const response = await fetch(imagePath, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

// Generate document viewer URL
export const getDocumentViewerUrl = (documentId: string): string => {
  return `/document/${documentId}`;
};

// Search utility for filtering documents
export const searchDocuments = (documents: Document[], query: string): Document[] => {
  if (!query.trim()) return documents;
  
  const searchTerm = query.toLowerCase();
  return documents.filter(doc => 
    doc.filename.toLowerCase().includes(searchTerm) ||
    doc.id.includes(searchTerm) ||
    (doc.extractedText && doc.extractedText.toLowerCase().includes(searchTerm))
  );
};

// Paginate documents
export const paginateDocuments = <T>(items: T[], page: number, itemsPerPage: number): {
  items: T[];
  totalPages: number;
  currentPage: number;
  hasNext: boolean;
  hasPrev: boolean;
} => {
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = items.slice(startIndex, endIndex);
  const totalPages = Math.ceil(items.length / itemsPerPage);
  
  return {
    items: paginatedItems,
    totalPages,
    currentPage: page,
    hasNext: page < totalPages,
    hasPrev: page > 1,
  };
};